import { Debt } from '@/hooks/useDebts';

export interface PayoffProjection {
  debtId: string;
  debtName: string;
  monthsToPayoff: number;
  payoffDate: Date;
  totalInterestPaid: number;
  totalPaid: number;
  order: number;
}

export interface SnowballSavings {
  totalInterestWithSnowball: number;
  totalInterestWithoutSnowball: number;
  interestSaved: number;
  interestSavedPercentage: number;
  monthsSaved: number;
  savingsPerDebt: Array<{
    debtId: string;
    debtName: string;
    interestSaved: number;
    interestSavedPercentage: number;
  }>;
}

// Sort debts by snowball method (smallest balance first)
export function sortBySnowball(debts: Debt[]): Debt[] {
  return [...debts]
    .filter(d => d.status === 'active')
    .sort((a, b) => a.current_balance - b.current_balance);
}

// Sort debts by avalanche method (highest interest first)
export function sortByAvalanche(debts: Debt[]): Debt[] {
  return [...debts]
    .filter(d => d.status === 'active')
    .sort((a, b) => b.interest_rate - a.interest_rate);
}

// Calculate monthly interest
function calculateMonthlyInterest(balance: number, annualRate: number): number {
  return balance * (annualRate / 100 / 12);
}

// ── Month-by-month snowball simulation ──────────────────────────────
// All debts are simulated simultaneously:
// 1. Each month, interest accrues on every active debt.
// 2. Minimum payments are applied to all active debts.
// 3. Any extra budget (extra payment + freed minimums from paid-off debts)
//    is applied to the target debt (smallest remaining balance).
export function calculateSnowballProjection(
  debts: Debt[],
  extraMonthlyPayment: number = 0
): PayoffProjection[] {
  const activeDebts = debts.filter(d => d.status === 'active');
  if (activeDebts.length === 0) return [];

  // Working state per debt
  const state = activeDebts.map(d => ({
    id: d.id,
    name: d.name,
    balance: d.current_balance,
    rate: d.interest_rate,
    minPayment: d.minimum_payment,
    totalInterest: 0,
    totalPaid: 0,
    paidOffMonth: -1,
  }));

  const startDate = new Date();
  const maxMonths = 360; // 30 year cap
  let month = 0;
  let payoffOrder = 1;

  // Check for debts whose minimum payment can't cover interest (infinite payoff)
  for (const s of state) {
    if (s.balance <= 0.01) {
      s.paidOffMonth = 0;
      continue;
    }
    const firstInterest = calculateMonthlyInterest(s.balance, s.rate);
    if (s.minPayment <= firstInterest && s.rate > 0 && extraMonthlyPayment === 0) {
      // Even with snowball, if it's the first target and payment can't beat interest
      // we'll let the simulation run and catch it via maxMonths
    }
  }

  while (state.some(s => s.balance > 0.01 && s.paidOffMonth < 0) && month < maxMonths) {
    month++;

    // 1. Accrue interest on all active debts
    for (const s of state) {
      if (s.balance <= 0.01) continue;
      const interest = calculateMonthlyInterest(s.balance, s.rate);
      s.balance += interest;
      s.totalInterest += interest;
    }

    // 2. Calculate total available payment this month
    //    = sum of all minimums + extra + freed minimums from paid-off debts
    //    Freed minimums: debts already paid off no longer need their minimum,
    //    that money rolls into the snowball.

    // Apply minimum payment to every active debt first
    for (const s of state) {
      if (s.balance <= 0.01) continue;
      const payment = Math.min(s.minPayment, s.balance);
      s.balance -= payment;
      s.totalPaid += payment;

      if (s.balance <= 0.01) {
        s.balance = 0;
        s.paidOffMonth = month;
      }
    }

    // 3. Calculate snowball extra: user extra + freed minimums from debts paid off BEFORE this month
    const freedMinimums = state
      .filter(s => s.paidOffMonth >= 0 && s.paidOffMonth < month)
      .reduce((sum, s) => sum + s.minPayment, 0);

    let snowballExtra = extraMonthlyPayment + freedMinimums;

    // Also add back minimums from debts that just got paid off this month
    // (any leftover from overpaying the minimum is already handled by Math.min above)
    // The freed minimum from debts paid THIS month will be available NEXT month
    // (this is the standard snowball convention)

    // 4. Apply snowball extra to the target debt (smallest active balance)
    while (snowballExtra > 0.01) {
      // Find target: smallest balance among still-active debts
      const target = state
        .filter(s => s.balance > 0.01)
        .sort((a, b) => a.balance - b.balance)[0];

      if (!target) break;

      const payment = Math.min(snowballExtra, target.balance);
      target.balance -= payment;
      target.totalPaid += payment;
      snowballExtra -= payment;

      if (target.balance <= 0.01) {
        target.balance = 0;
        target.paidOffMonth = month;
        // Freed minimum available next month — already handled by the freedMinimums calc
      }
    }
  }

  // Build projections sorted by payoff order
  const results = state
    .filter(s => s.paidOffMonth >= 0)
    .sort((a, b) => a.paidOffMonth - b.paidOffMonth);

  // Debts that never got paid off
  const unpaid = state.filter(s => s.paidOffMonth < 0);

  const projections: PayoffProjection[] = [];

  for (const s of results) {
    const payoffDate = new Date(startDate);
    payoffDate.setMonth(payoffDate.getMonth() + s.paidOffMonth);

    projections.push({
      debtId: s.id,
      debtName: s.name,
      monthsToPayoff: s.paidOffMonth,
      payoffDate,
      totalInterestPaid: s.totalInterest,
      totalPaid: s.totalPaid,
      order: payoffOrder++,
    });
  }

  for (const s of unpaid) {
    projections.push({
      debtId: s.id,
      debtName: s.name,
      monthsToPayoff: Infinity,
      payoffDate: new Date(9999, 11, 31),
      totalInterestPaid: Infinity,
      totalPaid: Infinity,
      order: payoffOrder++,
    });
  }

  return projections;
}

// Calculate payoff for a single debt with only minimum payments (no snowball)
function calculateSingleDebtPayoff(
  balance: number,
  minimumPayment: number,
  annualInterestRate: number
): { months: number; totalInterest: number } {
  if (balance <= 0) return { months: 0, totalInterest: 0 };

  let remaining = balance;
  let totalInterest = 0;
  let months = 0;
  const maxMonths = 360;

  while (remaining > 0.01 && months < maxMonths) {
    const interest = calculateMonthlyInterest(remaining, annualInterestRate);
    totalInterest += interest;
    remaining = remaining + interest - minimumPayment;
    months++;

    if (minimumPayment <= interest && annualInterestRate > 0) {
      return { months: Infinity, totalInterest: Infinity };
    }
  }

  return { months, totalInterest };
}

// Calculate savings comparing snowball vs minimum payments only
export function calculateSnowballSavings(
  debts: Debt[],
  extraMonthlyPayment: number = 0
): SnowballSavings {
  const activeDebts = debts.filter(d => d.status === 'active');

  // With snowball
  const snowballProjection = calculateSnowballProjection(activeDebts, extraMonthlyPayment);
  const totalInterestWithSnowball = snowballProjection.reduce(
    (sum, p) => sum + (p.totalInterestPaid === Infinity ? 0 : p.totalInterestPaid),
    0
  );
  const maxMonthsSnowball = Math.max(
    ...snowballProjection.map(p => (p.monthsToPayoff === Infinity ? 0 : p.monthsToPayoff)),
    0
  );

  // Without snowball (each debt pays only its own minimum, no rolling)
  let totalInterestWithoutSnowball = 0;
  let maxMonthsNoSnowball = 0;

  const savingsPerDebt: SnowballSavings['savingsPerDebt'] = [];

  activeDebts.forEach(debt => {
    const solo = calculateSingleDebtPayoff(
      debt.current_balance,
      debt.minimum_payment,
      debt.interest_rate
    );

    const snowballDebt = snowballProjection.find(p => p.debtId === debt.id);
    const snowballInterest = snowballDebt?.totalInterestPaid || 0;

    totalInterestWithoutSnowball += solo.totalInterest === Infinity ? 0 : solo.totalInterest;
    maxMonthsNoSnowball = Math.max(
      maxMonthsNoSnowball,
      solo.months === Infinity ? 0 : solo.months
    );

    if (solo.totalInterest !== Infinity && snowballInterest !== Infinity) {
      const saved = solo.totalInterest - snowballInterest;
      savingsPerDebt.push({
        debtId: debt.id,
        debtName: debt.name,
        interestSaved: Math.max(0, saved),
        interestSavedPercentage:
          solo.totalInterest > 0 ? Math.max(0, (saved / solo.totalInterest) * 100) : 0,
      });
    }
  });

  const interestSaved = totalInterestWithoutSnowball - totalInterestWithSnowball;
  const interestSavedPercentage =
    totalInterestWithoutSnowball > 0
      ? (interestSaved / totalInterestWithoutSnowball) * 100
      : 0;

  return {
    totalInterestWithSnowball,
    totalInterestWithoutSnowball,
    interestSaved: Math.max(0, interestSaved),
    interestSavedPercentage: Math.max(0, interestSavedPercentage),
    monthsSaved: Math.max(0, maxMonthsNoSnowball - maxMonthsSnowball),
    savingsPerDebt,
  };
}

// Generate month-by-month balance timeline for charting
export interface MonthlyBalancePoint {
  month: number;
  label: string;
  totalBalance: number;
  [debtName: string]: number | string;
}

export function generateBalanceTimeline(
  debts: Debt[],
  extraMonthlyPayment: number = 0
): MonthlyBalancePoint[] {
  const activeDebts = debts.filter(d => d.status === 'active');
  if (activeDebts.length === 0) return [];

  const state = activeDebts.map(d => ({
    id: d.id,
    name: d.name,
    balance: d.current_balance,
    rate: d.interest_rate,
    minPayment: d.minimum_payment,
    paidOffMonth: -1,
  }));

  const startDate = new Date();
  const maxMonths = 360;
  const timeline: MonthlyBalancePoint[] = [];

  // Month 0
  const point0: MonthlyBalancePoint = {
    month: 0,
    label: 'Hoy',
    totalBalance: state.reduce((s, d) => s + d.balance, 0),
  };
  state.forEach(s => { point0[s.name] = Math.round(s.balance); });
  timeline.push(point0);

  for (let month = 1; month <= maxMonths; month++) {
    // Accrue interest
    for (const s of state) {
      if (s.balance <= 0.01) continue;
      s.balance += s.balance * (s.rate / 100 / 12);
    }

    // Pay minimums
    for (const s of state) {
      if (s.balance <= 0.01) continue;
      const pay = Math.min(s.minPayment, s.balance);
      s.balance -= pay;
      if (s.balance <= 0.01) { s.balance = 0; s.paidOffMonth = month; }
    }

    // Snowball extra
    const freed = state
      .filter(s => s.paidOffMonth >= 0 && s.paidOffMonth < month)
      .reduce((sum, s) => sum + s.minPayment, 0);
    let extra = extraMonthlyPayment + freed;

    const active = state.filter(s => s.balance > 0.01).sort((a, b) => a.balance - b.balance);
    for (const target of active) {
      if (extra <= 0.01) break;
      const pay = Math.min(extra, target.balance);
      target.balance -= pay;
      extra -= pay;
      if (target.balance <= 0.01) { target.balance = 0; target.paidOffMonth = month; }
    }

    const date = new Date(startDate);
    date.setMonth(date.getMonth() + month);
    const label = date.toLocaleDateString('es-CR', { month: 'short', year: '2-digit' });

    const point: MonthlyBalancePoint = {
      month,
      label,
      totalBalance: Math.round(state.reduce((s, d) => s + d.balance, 0)),
    };
    state.forEach(s => { point[s.name] = Math.round(s.balance); });
    timeline.push(point);

    if (state.every(s => s.balance <= 0.01)) break;
  }

  return timeline;
}

// Format months to years and months
export function formatMonthsToYearsMonths(totalMonths: number): string {
  if (totalMonths === Infinity) return 'Nunca';
  const years = Math.floor(totalMonths / 12);
  const months = Math.round(totalMonths % 12);

  if (years === 0) return `${months} mes${months !== 1 ? 'es' : ''}`;
  if (months === 0) return `${years} año${years !== 1 ? 's' : ''}`;
  return `${years} año${years !== 1 ? 's' : ''} y ${months} mes${months !== 1 ? 'es' : ''}`;
}
